import { CoauthorInformation } from '@ms/acui-collab';
import { GraphPersonPicture, getUserPhoto, resolvePresenceColor, isNumber } from '@fluidx/utilities';
import { TokenProvider, ITelemetryNamespaceLogger, PresenceColorProvider } from '@fluidx/office-fluid-types';

export type CoauthorUpdatesListener = (coauthors: CoauthorInformation[]) => void;
export type LastEditedUpdatesListener = (coauthor: CoauthorInformation) => void;

// ImageUrl cache for all coauthors in the current session to avoid re-fetching the imageUrl
// Kept global so all instances of the gallery can take advantage of this cache, since it doesn't need to be scoped
const coauthorImageUrlsCache: { [userId: string]: string } = {};
// Maximum number of images cached, by default is 200
let imageCacheLimit = 200;

// TODO: CoauthorGallery: Removes items from the cache if the limit is made smaller than the current size
export function setImageCacheLimit(numberOfItems: number) {
  imageCacheLimit = numberOfItems;
}

/**
 * Base Updater which contains the common functionalities between the explicit Updaters.
 */
abstract class BaseUpdater {
  /**
   * Constructor of the BaseUpdater.
   * @param presenceColorProvider The color provider used to ensure consistency from the gallery colors to document presence colors
   * @param listener The callback to update a listener to changes in the coauthors
   * @param pageLogger The pageLogger instance to use for logging.
   * @param tokenProvider The tokenProvider to use for grabbing coauthor pictures
   */
  constructor(
    protected presenceColorProvider: PresenceColorProvider,
    protected readonly pageLogger?: ITelemetryNamespaceLogger,
    protected readonly tokenProvider?: TokenProvider
  ) {}

  // Presence colors cache for all coauthors in the current session to avoid re-fetching the presence color
  protected coauthorPresenceColorsCache: { [userId: string]: string } = {};

  // Latest state of the coauthors.
  protected coauthors: { [id: string]: CoauthorInformation } = {};

  /**
   * Set the presenceColorProvider
   * @param presenceColorProvider The new presenceColorProvider
   */
  public setPresenceColorProvider(presenceColorProvider: PresenceColorProvider) {
    if (this.presenceColorProvider !== presenceColorProvider) {
      this.presenceColorProvider = presenceColorProvider;

      // Reset the cache if we see a new presence color provider
      this.coauthorPresenceColorsCache = {};
    }
  }

  /**
   * Get the coauthor information with the resolved color.
   * @param client The client we want to update.
   * @returns a promise that is resolved when the coauthor's color is resolved.
   * In case the color is cached, the promise will be resolved.
   */
  public async getUpdatedCoauthorWithColor(client: Omit<CoauthorInformation, 'color'>): Promise<CoauthorInformation> {
    const imageUrl = coauthorImageUrlsCache[client.id];

    const color = this.coauthorPresenceColorsCache[client.id];
    const updatedCoauthor: CoauthorInformation = imageUrl ? { ...client, imageUrl, color } : { ...client, color };

    if (!color) {
      const resolvedColor = await resolvePresenceColor(this.presenceColorProvider, client.id, this.pageLogger);
      this.coauthorPresenceColorsCache[client.id] = resolvedColor;
      updatedCoauthor.color = resolvedColor;
    }

    return updatedCoauthor;
  }

  /**
   * Get the updated coauthor with the image resolved.
   */
  async getUpdatedCoauthorWithImage(
    coauthorImageUrlPromise: Promise<GraphPersonPicture | number>
  ): Promise<CoauthorInformation | undefined> {
    const personPicture: GraphPersonPicture | number = await coauthorImageUrlPromise;
    let coauthorInfo: CoauthorInformation | undefined;

    if (!isNumber(personPicture)) {
      const userId = personPicture.userPrincipalName;
      if (Object.keys(coauthorImageUrlsCache).length < imageCacheLimit) {
        coauthorImageUrlsCache[userId] = personPicture.pictureUrl;
      }

      // Coauthors may have changed in the meantime (left, joined) so we consider only coauthors in the latest state.
      if (this.coauthors[userId]) {
        this.coauthors[userId] = { ...this.coauthors[userId], imageUrl: personPicture.pictureUrl };
        coauthorInfo = this.coauthors[userId];
      }
    }

    return coauthorInfo;
  }
}

/**
 * The CoauthorsUpdater keeps track of stored coauthors, colors, and notifies listeners when coauthors are updated.
 */
export class CoauthorsUpdater extends BaseUpdater {
  /**
   * Creates a new CoauthorsUpdater
   * @param presenceColorProvider The color provider used to ensure consistency from the gallery colors to document presence colors
   * @param listener The callback to update a listener to changes in the coauthors
   * @param pageLogger The pageLogger instance to use for logging.
   * @param tokenProvider The tokenProvider to use for grabbing coauthor pictures
   */
  constructor(
    presenceColorProvider: PresenceColorProvider,
    private listener: CoauthorUpdatesListener,
    pageLogger?: ITelemetryNamespaceLogger,
    tokenProvider?: TokenProvider
  ) {
    super(presenceColorProvider, pageLogger, tokenProvider);
  }

  // The current user's user id
  private currentAuthorUserId: string | undefined = undefined;

  /**
   * Set details about the current author of the Fluid session
   * @param userId The current author's unique userId
   */
  public setCurrentAuthor(userId: string) {
    this.currentAuthorUserId = userId;
  }

  /**
   * De-dupes multiple clients of a coauthor, removes the clients of the current fluid author, and stores the latest state of coauthors
   * Also, loads the picture URL for the client from the image cache if available in the cache
   * TODO: CoauthorGallery: Store the latest focus position for each coauthor when we implement 'jump to coauthor's position'
   * @param clients All active clients in the page. Each instance/tab of a page would be a different client.
   * @returns a promise that is resolved when the coauthor's images are loaded
   */
  public async updateCoauthors(clients: Array<Omit<CoauthorInformation, 'color'>>): Promise<void> {
    // Reset state every time to make sure we always have the latest set of coauthors
    const updatedCoauthors: { [id: string]: CoauthorInformation } = {};

    const coauthorImageUrlPromises: Promise<GraphPersonPicture | number>[] = [];
    const coauthorColorPromises: Promise<void>[] = [];

    clients.forEach((client) => {
      const shouldUpdateCoauthor = () => {
        const isNotCurrentAuthor = !this.isCurrentAuthor(client.id);
        const isFirstOccurrence = !updatedCoauthors[client.id];
        const activeStatusNotUpdated = !updatedCoauthors[client.id]?.activeStatus && client.activeStatus;
        return isNotCurrentAuthor && (isFirstOccurrence || activeStatusNotUpdated);
      };

      // If there are multiple clients for the same author, use the first active client for that author.
      if (shouldUpdateCoauthor()) {
        // Load the coauthor's image from the image cache. If not available, fire a promise to fetch the image.
        const imageUrl = coauthorImageUrlsCache[client.id];
        // User photo requires access to API that is not available for UnauthenticatedGuest user
        if (!imageUrl && this.tokenProvider && this.tokenProvider.identityType !== 'UnauthenticatedGuest') {
          // TODO: Investigate possibility of extending CoauthorInformation to include tenant id for coauthors
          // coming from external tenant. For now this call is bound to fail for external coauthor.
          coauthorImageUrlPromises.push(getUserPhoto(this.tokenProvider, client.id, this.pageLogger));
        }

        coauthorColorPromises.push(
          this.getUpdatedCoauthorWithColor(client).then((coauthorInfo) => {
            // Skip updating already stored coauthors.
            if (shouldUpdateCoauthor()) {
              updatedCoauthors[client.id] = coauthorInfo;
            }
          })
        );
      }
    });

    // Unlike the Promises for images, we do want to get the Presence color info immediately
    // even if it is a Promise (and in most realistic scenarios, fetching it
    // should be much faster than an image-fetch would be).
    await Promise.all(coauthorColorPromises);

    // At this point each coauthor is guaranteed to have a color assigned, so we can now update the listener.
    this.coauthors = updatedCoauthors;
    this.listener(Object.values(updatedCoauthors));

    if (coauthorImageUrlPromises.length > 0) {
      // Explicitly returning the promise of image fetching in case someone wants to wait for it.
      return this.updateCoauthorImages(coauthorImageUrlPromises);
    }
  }

  /**
   * Updates the coauthorImageUrl cache and coauthors state with imageUrls and triggers a re-render of the FacePile.
   *
   * We wait on a promise.all for all coauthor's images instead of updating as they arrive because:
   * - A UX update as each image arrives might distract the user to the UX updates and away from the document, leading to a non-ideal user experience.
   * - The images might arrive out of order from the FacePile coins, and out of order UX updates are non-ideal as well in terms of user experience.
   */
  async updateCoauthorImages(coauthorImageUrlPromises: Promise<GraphPersonPicture | number>[]): Promise<void> {
    const coauthorsWithImages: Promise<CoauthorInformation | undefined>[] = [];
    coauthorImageUrlPromises.forEach((promise) => {
      coauthorsWithImages.push(this.getUpdatedCoauthorWithImage(promise));
    });

    const updatedCoauthors = await Promise.all(coauthorsWithImages);

    // Check if the image promise resolves while the clients are still connected.
    // In case they left, we won't update the listener.
    let coauthorsUpdated = false;
    for (const coauthor of updatedCoauthors) {
      if (coauthor !== undefined) {
        coauthorsUpdated = true;
        break;
      }
    }

    if (coauthorsUpdated) {
      this.listener(Object.values(this.coauthors));
    }
  }

  private isCurrentAuthor(userId: string) {
    return this.currentAuthorUserId && userId === this.currentAuthorUserId;
  }
}

/**
 * The LastEditedUpdater keeps track of stored coauthor who edited last the container, colors, and notifies listeners when coauthor is updated.
 */
// TODO: Add Unit tests for this class. Tracked by VSO Task #4261804
export class LastEditedUpdater extends BaseUpdater {
  /**
   * Creates a new LastEditedUpdater
   * @param presenceColorProvider The color provider used to ensure consistency from the gallery colors to document presence colors
   * @param listener The callback to update a listener to changes in the coauthors
   * @param pageLogger The pageLogger instance to use for logging.
   * @param tokenProvider The tokenProvider to use for grabbing coauthor pictures
   */
  constructor(
    presenceColorProvider: PresenceColorProvider,
    private readonly listener: LastEditedUpdatesListener,
    readonly pageLogger?: ITelemetryNamespaceLogger,
    readonly tokenProvider?: TokenProvider
  ) {
    super(presenceColorProvider, pageLogger, tokenProvider);
  }

  /**
   * Updates the color & picture of the given client.
   * @param client The client we want to update.
   * @returns a promise that is resolved when the coauthor's images are loaded
   */
  public async updateCoauthor(client: Omit<CoauthorInformation, 'color'>): Promise<void> {
    const updatedCoauthor = await this.getUpdatedCoauthorWithColor(client);

    // Reset state of coauthors.
    this.coauthors = {
      [client.id]: updatedCoauthor
    };

    // At this point the coauthor is guaranteed to have a color assigned, so we can now update the listener.
    this.listener(updatedCoauthor);

    // Check if the image was already cached and if yes reuse it.
    const imageUrl = coauthorImageUrlsCache[client.id];

    // User photo requires access to API that is not available for UnauthenticatedGuest user
    if (!imageUrl && this.tokenProvider && this.tokenProvider.identityType !== 'UnauthenticatedGuest') {
      // Explicitly returning the promise of image fetching in case someone wants to wait for it.
      return this.updateCoauthorImage(getUserPhoto(this.tokenProvider, client.id, this.pageLogger));
    }
  }

  /**
   * Updates the coauthorImageUrl cache and coauthors state with imageUrls and triggers a re-render of the FacePile.
   */
  async updateCoauthorImage(coauthorImageUrlPromise: Promise<GraphPersonPicture | number>): Promise<void> {
    return this.getUpdatedCoauthorWithImage(coauthorImageUrlPromise).then((coauthorInfo) => {
      coauthorInfo && this.listener(coauthorInfo);
    });
  }
}
